import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Paperclip, Send } from 'lucide-react';
import { useLang } from '../../../i18n';
import { get, post } from '../../../services/api';
import Card from '../../../components/Card';
import Button from '../../../components/Button';
import Textarea from '../../../components/Textarea';
import Skeleton from '../../../components/Skeleton';

interface ChatMessage {
  id: string;
  author_type?: 'customer' | 'admin' | 'system';
  author_name: string;
  body: string;
  created_at: string;
  is_mine?: boolean;
  is_internal?: boolean;
}

interface Props { publicId: string }

const POLL_MS = 4000;

export default function OrderChat({ publicId }: Props) {
  const { t } = useLang();
  const [items, setItems] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      const res = await get<{ results: ChatMessage[] } | ChatMessage[]>(`/me/orders/${publicId}/chat/`);
      const list = Array.isArray(res) ? res : res.results || [];
      setItems(list);
    } catch { void 0; }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Polling — самый надёжный вариант. WebSocket доступен через Channels,
    // но нестабильно в текущем nginx-конфиге, поэтому отключён.
    const id = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(id);
  }, [publicId]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [items]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSending(true);
    try {
      const msg = await post<ChatMessage>(`/me/orders/${publicId}/chat/`, { body: draft.trim() });
      if (msg) setItems((arr) => [...arr, msg]);
      setDraft('');
    } catch { void 0; }
    setSending(false);
  };

  const onAttach = async (files: FileList | null) => {
    if (!files || !files[0]) return;
    const fd = new FormData();
    fd.append('file', files[0]);
    try {
      await post(`/me/orders/${publicId}/chat/attachments/`, fd);
      load();
    } catch { void 0; }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    onAttach(e.dataTransfer.files);
  };

  return (
    <Card padding="none" className="order-chat">
      <div className="order-chat__list" ref={listRef} onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
        {loading ? <Skeleton height={120} /> : items.length === 0 ? (
          <p className="order-chat__empty">—</p>
        ) : items.map((m) => (
          <div key={m.id} className={['order-chat__msg', m.is_mine ? 'order-chat__msg--mine' : ''].filter(Boolean).join(' ')}>
            <div className="order-chat__msg-head">
              <span className="order-chat__msg-author">{m.author_name}</span>
              <span className="order-chat__msg-time">{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <div className="order-chat__msg-body">{m.body}</div>
          </div>
        ))}
      </div>
      <hr className="order-chat__divider" />
      <form className="order-chat__form" onSubmit={onSubmit}>
        <button type="button" className="order-chat__attach" aria-label={t('order.chat.attach')} onClick={() => fileRef.current?.click()}>
          <Paperclip size={18} />
        </button>
        <input ref={fileRef} type="file" hidden onChange={(e) => onAttach(e.target.files)} />
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('order.chat.placeholder')}
          maxHeight={120}
          className="order-chat__textarea"
        />
        <Button type="submit" variant="primary" size="md" loading={sending} iconLeft={<Send size={16} />}>
          {t('order.chat.send')}
        </Button>
      </form>
    </Card>
  );
}
